import { useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { setModal } from 'store/features/modelSlice';
import { appointmentStatusEnum, initModal, usersRoles } from 'helpers/utils';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import Textarea from 'components/common/textarea-field';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import Select, { IDefaultOption } from 'components/common/select';
import CheckBox from 'components/common/checkbox';
import CloudUpload from 'components/common/cloud-upload';
import { ReactComponent as SearchIcon } from 'assets/icon/search.svg';
import { ReactComponent as CancelIcon } from 'assets/icon/cancel.svg';
import { ReactComponent as PlayIcon } from 'assets/icon/play.svg';
import { setPreviewFileData } from 'store/features/modelSlice';
import { IUploadMediaResponse } from 'components/sidemodal/add-edit-workshop-appointment';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import { getWorkshopAppointmentList } from 'store/features/workshopAppointmentsSlice';
import { AppContext } from 'context/app-context';
import {
  getDealerAdminUsers,
  getSuperAdminUsers,
  IGetUserAPIParams,
} from 'store/features/userSlice';
import {
  ICreatePaymentData,
  ICreatePaymentRequestData,
  IPaymentDataError,
} from 'interfaces/payment-interface';
import MaskedNumberInput from 'components/common/currency-input';
import { IClientUserListItem } from 'interfaces/users-interface';
import { createPayment, uploadPaymentMedia } from 'store/features/paymentSlice';
import moment from 'moment';
import { IWorkshopAppointmentListItem } from 'interfaces/workshop-appointment-interface';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from 'hooks/useNumberFormat';

const AddPayment = () => {
  const dispatch = useAppDispatch();
  const { selectedOrganization } = useAppSelector(state => state.auth);
  const { users } = useAppSelector(state => state.user);
  const { workshopAppointments } = useAppSelector(state => state.workShopAppointment);
  const {
    modal: {
      content: { callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { setLoader } = useContext(AppContext);
  const { t } = useTranslation();
  const { toApiDecimal } = useNumberFormat();
  const [files, setFiles] = useState<File[]>([]);
  const [payementData, setPaymentData] = useState<ICreatePaymentData>({
    amount: '',
    description: '',
    user: null,
    workshopAppointment: null,
    vehicle: null,
  });
  const [usersOptions, setUsersOptions] = useState<IDefaultOption[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<IDefaultOption[]>([]);
  const [workshopAppointmentOptions, setWorkshopAppointmentOptions] = useState<IDefaultOption[]>(
    [],
  );
  const [selectedWorkshopAppointment, setSelectedWorkshopAppointment] =
    useState<IWorkshopAppointmentListItem | null>(null);
  const [selectedUser, setSelectedUser] = useState<IClientUserListItem | null>(null);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [isWorkshopPayment, setWorkshopPayment] = useState<boolean>(true);
  const [errors, setErrors] = useState<IPaymentDataError | null>(null);

  const uploadMediaForAdd = async () => {
    try {
      const uploadMediaParam =
        files.map(data => {
          return {
            request: {
              file_name: data?.name || '',
              file_type: data?.type || '',
              file_size: data?.size || 0,
            },
            file: data,
          };
        }) || [];
      const mediaResponse: IUploadMediaResponse[] = await Promise.all(
        [...uploadMediaParam].map(data => {
          const params: IUploadImageRequestParam = {
            request: {
              file_name: data?.request?.file_name || '',
              file_type: data?.request?.file_type || '',
              file_size: data?.request?.file_size || 0,
            },
            file: data?.file,
          };
          const res = dispatch(uploadPaymentMedia({ params }));
          return res;
        }),
      );
      return Promise.resolve(mediaResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const isValid = () => {
    if (!isWorkshopPayment) {
      if (!selectedUser || !payementData?.amount.trim()) {
        setErrors({
          ...errors,
          amount: !payementData?.amount.trim()
            ? t('payments.addPayment.errorAmountToastMessage')
            : '',
          user: !selectedUser ? t('payments.addPayment.errorSelectUserToastMessage') : '',
        });
        return false;
      } else {
        setErrors(null);
        return true;
      }
    } else {
      if (!payementData?.workshopAppointment?.id || !payementData?.amount.trim()) {
        setErrors({
          ...errors,
          amount: !payementData?.amount.trim()
            ? t('payments.addPayment.errorAmountToastMessage')
            : '',
          workshop: !payementData?.workshopAppointment?.id
            ? t('payments.addPayment.errorWorkshopToastMessage')
            : '',
        });
        return false;
      } else {
        setErrors(null);
        return true;
      }
    }
  };
  const handleSubmit = async () => {
    try {
      if (!isValid()) {
        return;
      }
      setLoader(true);

      const uploadMedia = files.length
        ? await uploadMediaForAdd().catch(err => {
            return;
          })
        : [];

      // Convert locale-formatted amount (e.g. "12.243.343,12" or "12,243,343.12") to
      // English decimal ("12243343.12") for the API.
      const apiAmount = toApiDecimal(payementData.amount);

      let request: ICreatePaymentRequestData = {
        amount: apiAmount,
      };
      if (uploadMedia?.length) {
        request = {
          ...request,
          media: uploadMedia?.length ? uploadMedia.map(data => data?.key || '') : [],
        };
      }
      if (!isWorkshopPayment) {
        request = {
          ...request,
          organization_id: selectedUser?.organization_id || selectedUser?.organizationDetails?._id,
          user_id: selectedUser?.id || '',
        };
        if (payementData?.vehicle?.id) {
          request = { ...request, vehicle_id: payementData?.vehicle?.id || '' };
        }
      } else {
        request = {
          ...request,
          workshop_id: payementData?.workshopAppointment?.id,
          organization_id: selectedWorkshopAppointment?.vehicle_data?.organization_id || '',
          user_id:
            selectedWorkshopAppointment?.user_id ||
            selectedWorkshopAppointment?.user_details?._id ||
            '',
          vehicle_id: selectedWorkshopAppointment?.vehicle_data?._id || '',
        };
      }
      if (payementData?.description) {
        request = { ...request, description: payementData?.description || '' };
      }
      await dispatch(
        createPayment({
          request: { ...request },
        }),
      )
        .then(() => {
          toast.success(t('payments.addPayment.successPaymentCreatedToastMessage'));
          callBackFun && callBackFun();
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSetFiles = (file: File) => {
    const updatedFiles = [...files, file];
    setFiles(updatedFiles);
  };
  const handlePreviewFile = (file: File, type: string) => {
    dispatch(
      setPreviewFileData({
        type: type || '',
        content: { url: window.URL.createObjectURL(file) },
      }),
    );
  };
  const getFileContent = (file: File) => {
    const splitedVal = file.type.split('/');
    switch (splitedVal[0]) {
      case 'image':
        return (
          <img
            src={window.URL.createObjectURL(file)}
            className="flex items-center justify-center min-w-full h-full w-12 object-contain"
            alt=""
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          />
        );
      case 'video':
        return (
          <div
            className="flex items-center justify-center bg-grey3 w-12 h-full min-w-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <PlayIcon />
          </div>
        );
      default:
        return (
          <div
            className="flex items-center justify-center bg-grey1 w-12 min-w-full h-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <SearchIcon />
          </div>
        );
    }
  };
  const handleRemoveFiles = (index: number) => {
    const updatedFiles = (files || []).filter((file, i) => i !== index);
    setFiles(updatedFiles);
  };
  const fetchSuperAdminUsers = () => {
    setLoader(true);
    dispatch(getSuperAdminUsers({}))
      .then(() => {
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const fetchDealerAdminUsers = () => {
    setLoader(true);
    let params: IGetUserAPIParams = selectedOrganization?.organization_id
      ? {
          organization_id: selectedOrganization?.organization_id || '',
        }
      : {};
    if (selectedOrganization?.role_name === usersRoles.FLEET_MANAGE) {
      params = { ...params, company_id: selectedOrganization?.associated_company_id };
    }
    dispatch(
      getDealerAdminUsers({
        params: {
          ...params,
        },
      }),
    )
      .then(() => {
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const getUsersList = () => {
    switch (selectedOrganization?.role_name) {
      case usersRoles.SUPER_ADMIN: {
        fetchSuperAdminUsers();
        break;
      }
      default: {
        fetchDealerAdminUsers();
        break;
      }
    }
  };
  const fetchWorkshopAppointmentList = () => {
    dispatch(
      getWorkshopAppointmentList({}),
    )
      .then(() => {
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const fetchAllOptions = async () => {
    try {
      setLoader(true);
      const promises = [getUsersList(), fetchWorkshopAppointmentList()];
      Promise.all(promises).then(() => {
        setLoader(false);
      });
    } catch (error) {}
  };
  useEffect(() => {
    fetchAllOptions();
  }, []);
  useEffect(() => {
    const updatedUsersOptions: IDefaultOption[] =
      users.map(data => ({
        id: data?.id || '',
        name: `${data?.name} ${data?.surname} (${data?.email})` || '',
        value: data?.id || '',
      })) || [];
    setUsersOptions(updatedUsersOptions);
  }, [users]);
  useEffect(() => {
    const updatedWOrkshopAppointmentOptions: IDefaultOption[] =
      workshopAppointments.map(data => ({
        id: data?._id || '',
        name:
          `${
            data?.slot
              ? moment(`${data?.slot.date} ${data?.slot.start_time}`, 'YYYY-MM-DD HH:mm').format(
                  'MMM DD, YYYY HH:mm',
                )
              : ''
          } (${data?.user_details?.name} ${data?.user_details?.surname} - ${data?.vehicle_data
            ?.plate})` || '',
        value: data?._id || '',
      })) || [];
    setWorkshopAppointmentOptions(updatedWOrkshopAppointmentOptions);
  }, [workshopAppointments]);
  useEffect(() => {
    const updatedVehicleOptions =
      selectedUser?.vehicle_details.map(data => ({
        id: data?._id,
        // name: `${data?.model_data?.name} (${data?.plate})`,
        name: `${data?.model} (${data?.plate})`,
      })) || [];
    setVehicleOptions(updatedVehicleOptions);
    setPaymentData({ ...payementData, vehicle: null });
  }, [selectedUser]);
  return (
    <div className="flex flex-wrap max-w-[450px] w-[450px] max-h-[calc(100vh-60px)] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">
            {t('payments.addPayment.paymentTitle')}
          </label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh-200px)] gap-y-4">
        <div className="flex items-center gap-x-2 flex-wrap w-full">
          <CheckBox checked={isWorkshopPayment} onChange={checked => setWorkshopPayment(checked)} />
          <label className="font-semibold text-sm text-black2 flex-1">
            {t('payments.addPayment.createPaymentLabel')}
          </label>
        </div>
        <div className="flex w-full">
          <label className="text-sm text-black2 break-words font-medium">
            {t('payments.addPayment.uploadInvoiceLabel')}
          </label>
        </div>
        <div className="flex w-full">
          <CloudUpload
            setFile={e => {
              if (e?.target?.files?.length) {
                handleSetFiles(e?.target?.files[0]);
              } else {
                e.stopPropagation();
              }
            }}
            title={t('payments.addPayment.fileTitle')}
            acceptedFile=".pdf"
            isDisabled={files?.length > 0}
          />
        </div>
        {files && files?.length ? (
          <div className="w-full flex items-center justify-start overflow-hidden">
            <div className="overflow-x-auto w-full max-w-full flex py-2">
              {[...files].map((file, index) => (
                <div
                  className="flex items-center justify-center ml-2 rounded-3 h-12 w-12 max-w-12 min-h-full min-w-fit bg-grey1 relative"
                  key={`${file?.name}-${index}`}
                >
                  <CancelIcon
                    className="flex absolute -top-1 -right-1 cursor-pointer"
                    onClick={() => handleRemoveFiles(index)}
                  />
                  {getFileContent(file)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        <>
          {isWorkshopPayment ? (
            <div className="w-full">
              <label className="font-medium text-sm text-black2 w-full">
                {t('payments.addPayment.workshopLabel')}
              </label>
              <Select
                options={workshopAppointmentOptions}
                selected={payementData?.workshopAppointment || null}
                setSelected={selected => {
                  const selectedWorkshopApp =
                    workshopAppointments?.find(data => data?._id === selected?.id) || null;
                  setSelectedWorkshopAppointment(selectedWorkshopApp);
                  setPaymentData({
                    ...payementData,
                    workshopAppointment: selected,
                  });
                }}
                placeholder={t('payments.addPayment.workshopPlaceholder')}
              />
            </div>
          ) : (
            <>
              <div className="w-full">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('payments.addPayment.customerLabel')}:
                </label>
                <Select
                  options={
                    usersOptions.filter(
                      item => item?.name?.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                    ) || []
                  }
                  selected={payementData?.user}
                  setSelected={selected => {
                    const selectedUsr = users.find(data => data.id === selected.id) || null;
                    setSelectedUser(selectedUsr);
                    setPaymentData({ ...payementData, user: selected });
                  }}
                  placeholder={t('payments.addPayment.customerPlaceholder')}
                  isSearchable
                  search={dropdownSearch}
                  setSearch={setDropdownSearch}
                  showCross
                  handleCross={() => {
                    setPaymentData({ ...payementData, user: null });
                  }}
                />
              </div>
              <div className="w-full">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('payments.addPayment.vehicleLabel')}:
                </label>
                <Select
                  options={
                    vehicleOptions.filter(
                      item => item?.name?.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                    ) || []
                  }
                  selected={payementData?.vehicle}
                  setSelected={selected => {
                    setPaymentData({ ...payementData, vehicle: selected });
                  }}
                  placeholder={t('payments.addPayment.vehiclePlaceholder')}
                  isSearchable
                  search={dropdownSearch}
                  setSearch={setDropdownSearch}
                  showCross
                  handleCross={() => {
                    setPaymentData({ ...payementData, vehicle: null });
                  }}
                />
              </div>
            </>
          )}
        </>
        <div className="w-full">
          <label className="font-medium text-sm text-black2 w-full">
            {t('payments.tableHeaders.amount')}
          </label>
          <MaskedNumberInput
            placeholder={t('payments.addPayment.amountPlaceholder')}
            value={payementData.amount}
            onChange={val => {
              // Store Spanish-formatted value without the € prefix so the mask
              // can round-trip correctly (e.g. "12.243.343,12").
              // Conversion to English decimal happens only at API submission time.
              setPaymentData({ ...payementData, amount: val.replaceAll('€', '') });
            }}
          />
        </div>
        <div className="w-full">
          <label className="font-medium text-sm text-black2 w-full">
            {t('payments.tableHeaders.descriptionLabel')}
          </label>
          <Textarea
            placeholder={t('payments.tableHeaders.descriptionPlaceholder')}
            value={payementData.description}
            onChange={val => setPaymentData({ ...payementData, description: val })}
            row={2}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={t('payments.tableHeaders.confirm')}
          onClick={() => handleSubmit()}
          width="w-1/2"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddPayment;
